import { keyBy } from 'lodash';
import type { IUISupplier, Slug } from '@bridebook/toolbox/src/types';
import { IWeddingKeyword } from '@bridebook/web/components/keywords/types';
import { Chapter } from '@bridebook/web/lib/onboarding/chapters/Chapter';
import { Step } from '@bridebook/web/lib/onboarding/types';

export enum OnboardingAnalyticsAction {
  StartedUserOnboarding = 'Started user onboarding',
  FinishedUserOnboarding = 'Finished user onboarding',
  CompletedUserOnboardingStep = 'Completed user onboarding step',
  SkippedUserOnboardingStep = 'Skipped user onboarding step',
  EditedWeddingDetails = 'Edited wedding details',
  ClickedBackOnUserOnboardingStep = 'Clicked back on user onboarding step',
  LoadedSearchResults = 'Loaded search results',
  TriggeredWeddingInsurancePopup = 'Triggered wedding insurance popup',
  SelectedWeddingKeyword = 'Selected wedding keyword',
  ConfirmedWeddingKeyword = 'Confirmed wedding keywords',
  ClosedConciergeMatchesScreen = 'Closed concierge matches screen',
  CompletedOnboardingNameStep = 'Completed onboarding name step',
}

const skippedUserOnboardingStep = (payload: { currentStep: Step; currentChapter: Chapter }) => ({
  type: OnboardingAnalyticsAction.SkippedUserOnboardingStep,
  payload,
});

const clickedBackOnUserOnboardingStep = (payload: {
  currentStep: Step;
  currentChapter: Chapter;
}) => ({ type: OnboardingAnalyticsAction.ClickedBackOnUserOnboardingStep, payload });

const completedUserOnboardingStep = (payload: {
  currentStep: Step;
  currentChapter: Chapter;
  extraAnalytics?: { [key: string]: any };
}) => ({
  type: OnboardingAnalyticsAction.CompletedUserOnboardingStep,
  payload,
});

const loadedSearchResults = (payload: {
  searchActiveFilters: Record<string, any>;
  searchSortedBy: string;
  searchResultsTotal: number;
  list: IUISupplier[];
  searchSupplierCategory?: Slug;
  userId?: string;
  searchTerm?: string;
}) => ({
  type: OnboardingAnalyticsAction.LoadedSearchResults,
  payload,
});

const startedUserOnboarding = () => ({
  type: OnboardingAnalyticsAction.StartedUserOnboarding,
});

const triggeredWeddingInsurancePopup = () => ({
  type: OnboardingAnalyticsAction.TriggeredWeddingInsurancePopup,
});

const selectedWeddingKeyword = (payload: {
  selectedWeddingKeyword: string;
  selectedWeddingKeywordCategory: string;
  selectedWeddingKeywordId: string;
  weddingKeywordsLocation?: string;
}) => ({
  type: OnboardingAnalyticsAction.SelectedWeddingKeyword,
  payload: {
    ...payload,
    weddingKeywordsLocation: payload.weddingKeywordsLocation ?? 'onboarding',
    category: 'settings',
  },
});

const confirmedWeddingKeyword = (payload: {
  selectedWeddingKeywords: IWeddingKeyword[];
  weddingKeywordsLocation?: string;
}) => {
  const selectedKeywordsById = keyBy(
    payload.selectedWeddingKeywords.map((item) => ({
      id: item.id,
      keyword: item.keyword,
      category: item.category,
    })),
    'id',
  );
  return {
    type: OnboardingAnalyticsAction.ConfirmedWeddingKeyword,
    payload: {
      ...payload,
      selectedWeddingKeywords: selectedKeywordsById,
      weddingKeywordsCount: payload.selectedWeddingKeywords.length,
      weddingKeywordsLocation: payload.weddingKeywordsLocation ?? 'onboarding',
      category: 'settings',
    },
  };
};

const finishedUserOnboarding = (payload: { exitLocation?: string; skipped?: boolean }) => ({
  type: OnboardingAnalyticsAction.FinishedUserOnboarding,
  payload,
});

//Closed concierge matches screen
const closedConciergeMatchesScreen = () => ({
  type: OnboardingAnalyticsAction.ClosedConciergeMatchesScreen,
});

const completedOnboardingNameStep = (payload: {
  userNameCompletedFlag: boolean;
  partnerNameCompletedFlag: boolean;
}) => ({
  type: OnboardingAnalyticsAction.CompletedOnboardingNameStep,
  payload,
});

export const analytics = {
  skippedUserOnboardingStep,
  clickedBackOnUserOnboardingStep,
  completedUserOnboardingStep,
  loadedSearchResults,
  startedUserOnboarding,
  triggeredWeddingInsurancePopup,
  finishedUserOnboarding,
  selectedWeddingKeyword,
  confirmedWeddingKeyword,
  closedConciergeMatchesScreen,
  completedOnboardingNameStep,
};
